/**
 * Featured Filters
 *
 * Applies SearchSpring filters when clicking featured filter buttons.
 * Toggles filter in URL and dispatches FilterUpdate so results list and active facets stay in sync.
 * Disables buttons for filter values that would return 0 products (from facet counts).
 */

import { ThemeEvents } from '@theme/events';

const ACTIVE_CLASS = 'featured-filters--active';
const DISABLED_CLASS = 'featured-filters--disabled';

/** @type {Map<string, number>} field|value -> count from last ss:results-loaded */
let facetCountMap = new Map();

/**
 * Build map of "field|value" -> count from facets. Used to disable buttons with 0 results.
 * Keys use \n so we match exactly; SearchSpring returns field and value.value as-is.
 * @param {Array<{ field: string, values: Array<{ value: string, count?: number }> }>} facets
 */
function updateFacetCountMap(facets) {
  facetCountMap = new Map();
  if (!Array.isArray(facets)) return;
  facets.forEach((facet) => {
    const field = facet.field;
    (facet.values || []).forEach((v) => {
      const key = `${field}\n${v.value}`;
      facetCountMap.set(key, typeof v.count === 'number' ? v.count : 0);
    });
  });
}

// Register immediately so we never miss the event (it may fire before DOMContentLoaded).
document.addEventListener('ss:results-loaded', (e) => {
  const { facets } = e.detail || {};
  updateFacetCountMap(facets);
  syncActiveState();
});

/**
 * Get count for a field+value from the facet map (exact key, or case-insensitive value match).
 * @param {string} field
 * @param {string} value
 * @returns {number | undefined} count if found
 */
function getCountForFieldValue(field, value) {
  const exactKey = `${field}\n${value}`;
  if (facetCountMap.has(exactKey)) return facetCountMap.get(exactKey);
  const valueLower = value.toLowerCase();
  for (const [key, count] of facetCountMap) {
    if (key.startsWith(`${field}\n`) && key.slice(field.length + 1).toLowerCase() === valueLower)
      return count;
  }
  return undefined;
}

/**
 * Whether we have any facet data for this field (so we can treat "missing value" as 0 results).
 * @param {string} field
 * @returns {boolean}
 */
function hasFacetDataForField(field) {
  const prefix = `${field}\n`;
  for (const key of facetCountMap.keys()) {
    if (key.startsWith(prefix)) return true;
  }
  return false;
}

/**
 * Sync active state and disabled state of featured filter buttons from current URL and facet counts.
 * Disable when: we have facet data for this field and (count is 0 or value is missing from response).
 * SearchSpring often returns only facet values with count > 0, so "missing" means 0 results.
 */
function syncActiveState() {
  const urlParams = new URLSearchParams(window.location.search);
  document.querySelectorAll('[data-ss-featured-filter]').forEach((btn) => {
    const field = btn.getAttribute('data-filter-field');
    const value = btn.getAttribute('data-filter-value');
    if (field == null || value == null) return;
    const paramName = `filter.${field}`;
    const currentValues = urlParams.getAll(paramName);
    const isActive = currentValues.includes(value);
    btn.classList.toggle(ACTIVE_CLASS, isActive);
    btn.setAttribute('aria-pressed', String(isActive));

    const count = getCountForFieldValue(field, value);
    const hasDataForField = hasFacetDataForField(field);
    const hasZeroResults =
      (hasDataForField && count === undefined) || (count !== undefined && count === 0);
    const shouldDisable = hasZeroResults && !isActive;
    btn.classList.toggle(DISABLED_CLASS, shouldDisable);
    btn.disabled = shouldDisable;
  });
}

/**
 * Toggle a single filter in the URL: add if not present, remove if present.
 * @param {string} field - SearchSpring facet field name
 * @param {string} value - Filter value
 */
function toggleFilterInUrl(field, value) {
  const url = new URL(window.location.href);
  const paramName = `filter.${field}`;
  const values = url.searchParams.getAll(paramName);

  url.searchParams.delete(paramName);
  if (values.includes(value)) {
    values.filter((v) => v !== value).forEach((v) => url.searchParams.append(paramName, v));
  } else {
    values.forEach((v) => url.searchParams.append(paramName, v));
    url.searchParams.append(paramName, value);
  }
  url.searchParams.delete('page');

  history.pushState({}, '', url.toString());
  performance.mark('facet-filter-dispatched');
  document.dispatchEvent(new CustomEvent(ThemeEvents.FilterUpdate));
}

function init() {
  document.addEventListener('click', (e) => {
    const btn = e.target?.closest?.('[data-ss-featured-filter]');
    if (!btn || btn.disabled) return;
    const field = btn.getAttribute('data-filter-field');
    const value = btn.getAttribute('data-filter-value');
    if (field != null && value != null) {
      toggleFilterInUrl(field, value);
      syncActiveState();
    }
  });

  document.addEventListener(ThemeEvents.FilterUpdate, syncActiveState);
  syncActiveState();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
